import { useState, useMemo } from 'react';
import {
  FileText,
  Clock,
  Star,
  Archive,
  CheckSquare,
  MessageSquareText,
  Plus,
  ExternalLink,
  Pencil,
  ListChecks,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useTemplates,
  useArchiveTemplate,
  useCreateTemplate,
  useCloneTemplateToEvent,
  useUpdateTemplate,
} from '../hooks/useTemplates';
import type { TemplateType } from '../hooks/useTemplates';
import { useEvents } from '../hooks/useEvents';
import { EntityList, StatusBadge } from '../components/common';
import type { EntityListColumn } from '../components/common';
import { EntityModal } from '../components/common/EntityModal';
import type { TemplateItem } from '../api/templates';
import { ImportTimelineButton } from '../components/event-ops/ImportTimelineButton';

const TYPE_ICONS: Record<string, typeof FileText> = {
  timeline: Clock,
  checklist: CheckSquare,
  kibuddim: Star,
  questionnaire: MessageSquareText,
};

const TYPE_COLORS: Record<string, string> = {
  timeline: 'blue',
  checklist: 'green',
  kibuddim: 'purple',
  questionnaire: 'amber',
};

const TYPE_LABELS: Record<string, string> = {
  timeline: 'Timeline',
  checklist: 'Checklist',
  kibuddim: 'Kibuddim',
  questionnaire: 'Questionnaire',
};

const TYPE_DETAIL_PATH: Record<TemplateType, (eventId: string) => string> = {
  timeline: (id) => `/events/${id}/timeline`,
  checklist: (id) => `/events/${id}/checklist`,
  kibuddim: (id) => `/events/${id}/kibuddim`,
  questionnaire: (id) => `/events/${id}/questionnaires`,
};

export function TemplatesPage() {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [previewTemplate, setPreviewTemplate] = useState<TemplateItem | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTemplate, setEditTemplate] = useState<TemplateItem | null>(null);
  const [useTemplateTarget, setUseTemplateTarget] = useState<TemplateItem | null>(null);

  const { data: templates = [], isLoading } = useTemplates(typeFilter || undefined);
  const archiveTemplate = useArchiveTemplate();
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const cloneTemplate = useCloneTemplateToEvent();

  const filtered = useMemo(() => {
    if (!search) return templates;
    const term = search.toLowerCase();
    return templates.filter(
      (t) =>
        t.templateName?.toLowerCase().includes(term) ||
        t.title?.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term),
    );
  }, [templates, search]);

  const PAGE_SIZE = 25;
  const total = filtered.length;
  const items = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const columns: EntityListColumn<TemplateItem>[] = [
    {
      key: 'name',
      header: 'Template',
      render: (t) => {
        const Icon = TYPE_ICONS[t._type] || FileText;
        return (
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${TYPE_COLORS[t._type] || 'gray'}-100`}
            >
              <Icon size={16} className={`text-${TYPE_COLORS[t._type] || 'gray'}-600`} />
            </div>
            <div>
              <p className="text-gray-900 font-semibold">
                {t.templateName || t.title || 'Untitled'}
              </p>
              {t.description && (
                <p className="text-xs text-gray-500 truncate max-w-[250px]">{t.description}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'type',
      header: 'Type',
      render: (t) => (
        <StatusBadge label={TYPE_LABELS[t._type] || t._type} color={TYPE_COLORS[t._type]} />
      ),
      width: 'w-24',
    },
    {
      key: 'sections',
      header: 'Sections',
      render: (t) => <span className="text-sm text-gray-600">{t.sections?.length || 0}</span>,
      width: 'w-20',
      hideOnMobile: true,
    },
    {
      key: 'updated',
      header: 'Updated',
      render: (t) => (
        <span className="text-xs text-gray-500">
          {new Date(t.updatedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      ),
      width: 'w-28',
      hideOnMobile: true,
    },
  ];

  return (
    <div>
      <EntityList
        items={items}
        columns={columns}
        getKey={(t) => t.id}
        isLoading={isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Search templates..."
        onPreview={(t) => setPreviewTemplate(t)}
        title="Templates"
        titleActions={
          <div className="flex items-center gap-2">
            <ImportTimelineButton isTemplate={true} />
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
              data-testid="new-template-button"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">New Template</span>
            </button>
          </div>
        }
        filterBar={
          <div className="flex items-center gap-2">
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Types</option>
              <option value="timeline">Timeline</option>
              <option value="checklist">Checklist</option>
              <option value="kibuddim">Kibuddim</option>
              <option value="questionnaire">Questionnaire</option>
            </select>
          </div>
        }
        emptyMessage="No templates yet. Click 'New Template' to create one, or save an event's checklist/timeline/kibuddim as a template."
      />

      {/* Preview Modal */}
      <EntityModal
        open={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
        title={previewTemplate?.templateName || 'Template Details'}
        size="md"
        footer={
          previewTemplate ? (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setUseTemplateTarget(previewTemplate);
                  setPreviewTemplate(null);
                }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                data-testid="use-template-button"
              >
                <ExternalLink size={14} />
                Use Template
              </button>
              <button
                onClick={() => {
                  if (previewTemplate._type === 'timeline') {
                    navigate(`/templates/timeline/${previewTemplate.id}`);
                  } else if (previewTemplate._type === 'questionnaire') {
                    navigate(`/templates/questionnaire/${previewTemplate.id}`);
                  } else {
                    setEditTemplate(previewTemplate);
                    setPreviewTemplate(null);
                  }
                }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
                data-testid="edit-template-button"
              >
                <Pencil size={14} />
                Edit
              </button>
              {previewTemplate._type === 'checklist' && (
                // The name is edited above; its sections and tasks, where
                // every checklist is edited.
                <button
                  onClick={() => navigate(`/checklists?templates=1&open=${previewTemplate.id}`)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
                  data-testid="edit-template-tasks-button"
                >
                  <ListChecks size={14} />
                  Edit tasks
                </button>
              )}
              <button
                onClick={() => {
                  archiveTemplate.mutate({
                    id: previewTemplate.id,
                    type: previewTemplate._type,
                  });
                  setPreviewTemplate(null);
                }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
              >
                <Archive size={14} />
                Archive
              </button>
            </div>
          ) : undefined
        }
      >
        {previewTemplate && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <StatusBadge
                label={TYPE_LABELS[previewTemplate._type]}
                color={TYPE_COLORS[previewTemplate._type]}
              />
              {previewTemplate.eventType && (
                <StatusBadge label={previewTemplate.eventType.name} color="gray" />
              )}
              <span className="text-xs text-gray-400">
                {previewTemplate.sections?.length || 0} sections
              </span>
            </div>
            {previewTemplate.description && (
              <p className="text-sm text-gray-600">{previewTemplate.description}</p>
            )}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Sections</h4>
              {(
                previewTemplate.sections as {
                  id?: string;
                  name?: string;
                  items?: unknown[];
                  entries?: unknown[];
                  roles?: unknown[];
                  questions?: unknown[];
                }[]
              )?.map((section, i) => (
                <div key={section.id || i} className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{section.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {section.items?.length ||
                      section.entries?.length ||
                      section.roles?.length ||
                      section.questions?.length ||
                      0}{' '}
                    items
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </EntityModal>

      {/* Create Template Modal */}
      <CreateTemplateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={(input) =>
          createTemplate.mutate(input, {
            onSuccess: () => setShowCreate(false),
          })
        }
        submitting={createTemplate.isPending}
      />

      {/* Edit Template Modal */}
      <EditTemplateModal
        template={editTemplate}
        onClose={() => setEditTemplate(null)}
        onSubmit={(templateName, description) => {
          if (!editTemplate) return;
          updateTemplate.mutate(
            {
              id: editTemplate.id,
              type: editTemplate._type,
              templateName,
              description: description || null,
            },
            { onSuccess: () => setEditTemplate(null) },
          );
        }}
        submitting={updateTemplate.isPending}
      />

      {/* Use Template Modal */}
      <UseTemplateModal
        template={useTemplateTarget}
        onClose={() => setUseTemplateTarget(null)}
        onSubmit={(eventId, title) => {
          if (!useTemplateTarget) return;
          cloneTemplate.mutate(
            {
              templateId: useTemplateTarget.id,
              type: useTemplateTarget._type,
              eventId,
              title,
            },
            {
              onSuccess: () => {
                const path = TYPE_DETAIL_PATH[useTemplateTarget._type](eventId);
                setUseTemplateTarget(null);
                navigate(path);
              },
            },
          );
        }}
        submitting={cloneTemplate.isPending}
      />
    </div>
  );
}

interface CreateTemplateInput {
  type: TemplateType;
  templateName: string;
  description?: string;
}

function EditTemplateModal({
  template,
  onClose,
  onSubmit,
  submitting,
}: {
  template: TemplateItem | null;
  onClose: () => void;
  onSubmit: (templateName: string, description: string) => void;
  submitting: boolean;
}) {
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');

  // Sync fields when a template is opened for editing
  const prevIdRef = useState<string | undefined>(undefined);
  if (template && prevIdRef[0] !== template.id) {
    prevIdRef[1](template.id);
    setTemplateName(template.templateName || '');
    setDescription(template.description || '');
  }

  function handleClose() {
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!templateName.trim()) return;
    onSubmit(templateName.trim(), description.trim());
  }

  return (
    <EntityModal
      open={template !== null}
      onClose={handleClose}
      title="Edit Template"
      size="md"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-template-form"
            disabled={submitting || !templateName.trim()}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="edit-template-submit"
          >
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      }
    >
      <form id="edit-template-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="edit-template-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Template Name
          </label>
          <input
            id="edit-template-name"
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label
            htmlFor="edit-template-desc"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Description (optional)
          </label>
          <textarea
            id="edit-template-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </form>
    </EntityModal>
  );
}

function CreateTemplateModal({
  open,
  onClose,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateTemplateInput) => void;
  submitting: boolean;
}) {
  const [type, setType] = useState<TemplateType>('checklist');
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');

  function reset() {
    setType('checklist');
    setTemplateName('');
    setDescription('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!templateName.trim()) return;
    onSubmit({
      type,
      templateName: templateName.trim(),
      description: description.trim() || undefined,
    });
  }

  return (
    <EntityModal
      open={open}
      onClose={handleClose}
      title="New Template"
      size="md"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-template-form"
            disabled={submitting || !templateName.trim()}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="create-template-submit"
          >
            {submitting ? 'Creating…' : 'Create Template'}
          </button>
        </div>
      }
    >
      <form id="create-template-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="template-type" className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <select
            id="template-type"
            value={type}
            onChange={(e) => setType(e.target.value as TemplateType)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="checklist">Checklist</option>
            <option value="timeline">Timeline</option>
            <option value="kibuddim">Kibuddim</option>
            <option value="questionnaire">Questionnaire</option>
          </select>
        </div>
        <div>
          <label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-1">
            Template Name
          </label>
          <input
            id="template-name"
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            required
            placeholder="e.g. Standard Wedding Checklist"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="template-desc" className="block text-sm font-medium text-gray-700 mb-1">
            Description (optional)
          </label>
          <textarea
            id="template-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </form>
    </EntityModal>
  );
}

function UseTemplateModal({
  template,
  onClose,
  onSubmit,
  submitting,
}: {
  template: TemplateItem | null;
  onClose: () => void;
  onSubmit: (eventId: string, title?: string) => void;
  submitting: boolean;
}) {
  const [eventId, setEventId] = useState('');
  const [title, setTitle] = useState('');
  const { data: events = [], isLoading } = useEvents();

  function handleClose() {
    setEventId('');
    setTitle('');
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!eventId) return;
    onSubmit(eventId, title.trim() || undefined);
  }

  return (
    <EntityModal
      open={template !== null}
      onClose={handleClose}
      title={template ? `Use Template: ${template.templateName || 'Untitled'}` : ''}
      size="md"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="use-template-form"
            disabled={submitting || !eventId}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="use-template-submit"
          >
            {submitting ? 'Cloning…' : 'Clone to Event'}
          </button>
        </div>
      }
    >
      <form id="use-template-form" onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Pick the event to clone this template into. The clone is independent of the template —
          editing it later won&apos;t affect the original.
        </p>
        <div>
          <label
            htmlFor="use-template-event"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Event
          </label>
          <select
            id="use-template-event"
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            disabled={isLoading}
            required
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
          >
            <option value="">{isLoading ? 'Loading events…' : 'Select an event…'}</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
                {ev.startAt ? ` — ${new Date(ev.startAt).toLocaleDateString()}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="use-template-title"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Title for the new copy (optional)
          </label>
          <input
            id="use-template-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={template?.title || template?.templateName || ''}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </form>
    </EntityModal>
  );
}
